import React, { useRef, useEffect, useState, useCallback } from 'react'
import {
  FilesetResolver,
  FaceLandmarker,
  FaceLandmarkerResult,
  NormalizedLandmark,
} from '@mediapipe/tasks-vision'

// Landmark indices for lip detection
const INNER_LIP_TOP = 13
const INNER_LIP_BOTTOM = 14
const FACE_LEFT = 130
const FACE_RIGHT = 359

// Detection parameters
const VARIATION_WINDOW_SEC = 0.5
const VARIATION_THRESHOLD = 0.0035
const MIN_SPEECH_SEC = 0.25
const MERGE_GAP_SEC = 0.15
const MAX_LIP_OPENING_THRESHOLD = 0.1

interface LipSpeechDetectionProps {
  videoRef: React.RefObject<HTMLVideoElement>
  isActive: boolean
  recordingStartRef?: React.MutableRefObject<number | null>
  onSpeechSegmentDetected?: (startTime: number, endTime: number) => void
  onSpeechStatusChange?: (isSpeaking: boolean) => void
  onCheatingEvent?: (type: string, metadata?: any) => void
}

interface MetricPoint {
  timestamp: number
  ratio: number
}

interface SpeechSegment {
  startTime: number
  endTime: number | null
}

const LipSpeechDetection: React.FC<LipSpeechDetectionProps> = ({
  videoRef,
  isActive,
  recordingStartRef,
  onSpeechSegmentDetected,
  onSpeechStatusChange,
  onCheatingEvent,
}) => {
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [isSpeaking, setIsSpeaking] = useState(false)
  const [currentRatio, setCurrentRatio] = useState(0)

  const faceLandmarkerRef = useRef<FaceLandmarker | null>(null)
  const metricsRef = useRef<MetricPoint[]>([])
  const currentSegmentRef = useRef<SpeechSegment | null>(null)
  const lastVideoTimeRef = useRef<number>(-1)
  const animationFrameRef = useRef<number | null>(null)
  const lastSpeakingStateRef = useRef<boolean>(false)

  // Initialize MediaPipe Face Landmarker
  useEffect(() => {
    if (!isActive) return

    const initializeMediaPipe = async () => {
      try {
        setIsLoading(true)
        setError(null)

        const vision = await FilesetResolver.forVisionTasks(
          'https://cdn.jsdelivr.net/npm/@mediapipe/tasks-vision@latest/wasm'
        )

        const faceLandmarker = await FaceLandmarker.createFromOptions(vision, {
          baseOptions: {
            modelAssetPath:
              'https://storage.googleapis.com/mediapipe-models/face_landmarker/face_landmarker/float16/1/face_landmarker.task',
            delegate: 'GPU',
          },
          runningMode: 'VIDEO',
          numFaces: 1,
          minFaceDetectionConfidence: 0.5,
          minFacePresenceConfidence: 0.5,
          minTrackingConfidence: 0.5,
        })

        faceLandmarkerRef.current = faceLandmarker
        setIsLoading(false)
      } catch (err) {
        console.error('Error initializing MediaPipe for lip detection:', err)
        setError(err instanceof Error ? err.message : 'Failed to initialize')
        setIsLoading(false)
      }
    }

    initializeMediaPipe()

    return () => {
      if (faceLandmarkerRef.current) {
        faceLandmarkerRef.current.close()
        faceLandmarkerRef.current = null
      }
    }
  }, [isActive])

  // Calculate Euclidean distance
  const getEuclideanDistance = (p1: NormalizedLandmark, p2: NormalizedLandmark): number => {
    return Math.sqrt(Math.pow(p1.x - p2.x, 2) + Math.pow(p1.y - p2.y, 2))
  }

  // Calculate lip opening ratio
  const calculateLipOpeningRatio = (landmarks: NormalizedLandmark[]): number => {
    try {
      const pTop = landmarks[INNER_LIP_TOP]
      const pBottom = landmarks[INNER_LIP_BOTTOM]
      const pLeft = landmarks[FACE_LEFT]
      const pRight = landmarks[FACE_RIGHT]

      if (!pTop || !pBottom || !pLeft || !pRight) return 0

      const verticalDist = getEuclideanDistance(pTop, pBottom)
      const horizontalFaceWidth = getEuclideanDistance(pLeft, pRight)

      if (horizontalFaceWidth === 0) return 0

      return verticalDist / horizontalFaceWidth
    } catch (err) {
      console.error('Error calculating lip opening ratio:', err)
      return 0
    }
  }

  // Calculate rolling standard deviation
  const calculateRollingStdDev = (windowSec: number): number => {
    const currentTime = metricsRef.current[metricsRef.current.length - 1]?.timestamp || 0
    const windowStart = currentTime - windowSec

    // Get metrics within the window
    const windowMetrics = metricsRef.current.filter((m) => m.timestamp >= windowStart)

    if (windowMetrics.length < 2) return 0

    // Calculate mean
    const mean = windowMetrics.reduce((sum, m) => sum + m.ratio, 0) / windowMetrics.length

    // Calculate variance
    const variance =
      windowMetrics.reduce((sum, m) => sum + Math.pow(m.ratio - mean, 2), 0) / windowMetrics.length

    // Return standard deviation
    return Math.sqrt(variance)
  }

  // Process detection results
  const processResults = useCallback(
    (results: FaceLandmarkerResult, currentTime: number) => {
      if (!results.faceLandmarks || results.faceLandmarks.length === 0) {
        // No face detected
        const ratio = 0
        metricsRef.current.push({ timestamp: currentTime, ratio })
        setCurrentRatio(ratio)

        // Check if we should end current speech segment
        if (currentSegmentRef.current) {
          endSpeechSegment(currentTime)
        }
        return
      }

      const landmarks = results.faceLandmarks[0]
      const ratio = calculateLipOpeningRatio(landmarks)

      // Store metric
      metricsRef.current.push({ timestamp: currentTime, ratio })
      setCurrentRatio(ratio)

      // Keep only last 2 seconds of data
      const cutoffTime = currentTime - 2.0
      metricsRef.current = metricsRef.current.filter((m) => m.timestamp >= cutoffTime)

      // Calculate variation
      const variation = calculateRollingStdDev(VARIATION_WINDOW_SEC)

      // Determine if speaking
      const speakingNow = variation > VARIATION_THRESHOLD && ratio < MAX_LIP_OPENING_THRESHOLD

      // Update speaking state
      if (speakingNow !== lastSpeakingStateRef.current) {
        setIsSpeaking(speakingNow)
        lastSpeakingStateRef.current = speakingNow

        if (onSpeechStatusChange) {
          onSpeechStatusChange(speakingNow)
        }
      }

      // Handle speech segment detection
      if (speakingNow) {
        if (!currentSegmentRef.current) {
          // Start new segment
          currentSegmentRef.current = {
            startTime: currentTime,
            endTime: null,
          }
        }
        // Update end time while speaking
        currentSegmentRef.current.endTime = currentTime
      } else if (currentSegmentRef.current && currentSegmentRef.current.endTime) {
        // Check if enough silence has passed to end segment
        const silenceDuration = currentTime - currentSegmentRef.current.endTime
        if (silenceDuration >= MERGE_GAP_SEC) {
          endSpeechSegment(currentSegmentRef.current.endTime)
        }
      }
    },
    [onSpeechSegmentDetected, onSpeechStatusChange, onCheatingEvent, recordingStartRef]
  )

  // End current speech segment
  const endSpeechSegment = (endTime: number) => {
    if (!currentSegmentRef.current) return

    const segment = currentSegmentRef.current
    const duration = endTime - segment.startTime

    // Check if segment meets minimum duration
    if (duration >= MIN_SPEECH_SEC) {
      if (onSpeechSegmentDetected) {
        onSpeechSegmentDetected(segment.startTime, endTime)
      }

      // Send cheating event for detected speech with the metadata format
      if (onCheatingEvent) {
        onCheatingEvent('SPEECH_DETECTED', {
          start_sec: segment.startTime,
          end_sec: endTime,
          duration: duration,
        })
      }
    }
    currentSegmentRef.current = null
  }

  // Detection loop
  const startDetectionLoop = useCallback(() => {
    if (!faceLandmarkerRef.current || !videoRef.current || animationFrameRef.current) {
      return
    }

    const detectLips = () => {
      if (!isActive || !faceLandmarkerRef.current) {
        stopDetectionLoop()
        return
      }

      const videoEl = videoRef.current
      if (
        !videoEl ||
        videoEl.paused ||
        videoEl.ended ||
        videoEl.readyState < videoEl.HAVE_CURRENT_DATA
      ) {
        animationFrameRef.current = window.requestAnimationFrame(detectLips)
        return
      }

      const videoCurrentTime = videoEl.currentTime
      const startTimeMs = performance.now()

      // Calculate time offset from interview start
      // Use optional chaining to safely access recordingStartRef.current
      const currentTime = recordingStartRef?.current
        ? (Date.now() - recordingStartRef.current) / 1000
        : videoCurrentTime

      if (currentTime > 0 && videoCurrentTime !== lastVideoTimeRef.current) {
        lastVideoTimeRef.current = videoCurrentTime

        try {
          const results = faceLandmarkerRef.current.detectForVideo(videoEl, startTimeMs)
          processResults(results, currentTime)
        } catch (error) {
          console.error('Error during lip detection:', error)
        }
      }

      animationFrameRef.current = window.requestAnimationFrame(detectLips)
    }

    animationFrameRef.current = window.requestAnimationFrame(detectLips)
  }, [videoRef, isActive, processResults])

  const stopDetectionLoop = useCallback(() => {
    if (animationFrameRef.current) {
      window.cancelAnimationFrame(animationFrameRef.current)
      animationFrameRef.current = null
    }
    lastVideoTimeRef.current = -1

    // End any ongoing speech segment
    if (currentSegmentRef.current && currentSegmentRef.current.endTime) {
      endSpeechSegment(currentSegmentRef.current.endTime)
    }
  }, [])

  // Start/stop detection based on active state
  useEffect(() => {
    if (isActive && !isLoading && !error && faceLandmarkerRef.current) {
      startDetectionLoop()
    } else {
      stopDetectionLoop()
    }

    return () => {
      stopDetectionLoop()
    }
  }, [isActive, isLoading, error, startDetectionLoop, stopDetectionLoop])

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      stopDetectionLoop()
      metricsRef.current = []
      currentSegmentRef.current = null
    }
  }, [stopDetectionLoop])

  return null
}

export default LipSpeechDetection
